import React from "react";

import { CalendarDay, PrayerHour } from "../../calendar/utils";
import { HymnPlayer } from "../../chants/audio/HymnPlayer";
import { PlayableTitle } from "../../chants/audio/PlayableTitle";
import { Chant } from "../../chants/Chant";
import { getNotesFromString } from "../../chants/utils";
import { PointedVerse, PointedWord } from "../../models";
import { StickyContainer } from "../../sticky/StickyContainer";
import { HYMNS } from "./hymns";
import { Rubric } from "./Rubric";

export const OfficeHymn: React.FC<{
    day: CalendarDay;
    hour: PrayerHour;
    verseLimit?: number;
    exportLines?: boolean;
}> = (props) => {
    const { day, hour, verseLimit, exportLines } = props;

    let mattinsId = "";
    let evensongId = "";
    let complineId = "to-thee-before-the-close-of-day";

    const hymnId = day.hymnId;

    if (hymnId === "apostle-or-evangelist") {
        mattinsId = "lord-of-creation-bow-thine-ear";
        evensongId = "lord-of-creation-bow-thine-ear";
    } else if (hymnId === "apostle-or-evangelist-easter") {
        mattinsId = "the-day-draws-on-apostles";
        evensongId = "lord-of-creation-bow-thine-ear";
    } else if (hymnId === "presentation") {
        mattinsId = "all-prophets-hail-thee";
        evensongId = "all-prophets-hail-thee";
    } else if (hymnId === "annunciation") {
        mattinsId = "the-god-whom-earth-and-sea-and-sky";
        evensongId = "the-god-whom-earth-and-sea-and-sky";
    } else if (hymnId === "lent-1") {
        mattinsId = "o-kind-creator-bow-thine-ear";
        evensongId = "the-fast-as-taught-by-holy-lore";
    } else if (hymnId === "lent-2") {
        mattinsId = "o-kind-creator-bow-thine-ear";
        evensongId = "the-fast-as-taught-by-holy-lore";
    } else if (hymnId === "lent-3") {
        mattinsId = "the-glory-of-these-forty-days";
        evensongId = "now-is-the-healing-time-decreed";
    } else if (hymnId === "lent-4") {
        mattinsId = "the-glory-of-these-forty-days";
        evensongId = "now-is-the-healing-time-decreed";
    } else if (hymnId === "passiontide-0") {
        mattinsId = "sing-my-tongue-the-glorious-battle";
        evensongId = "the-royal-banners-forward-go";
    } else if (hymnId === "passiontide-1") {
        mattinsId = "thirty-years-among-us-dwelling";
        evensongId = "the-royal-banners-forward-go";
    } else if (hymnId === "easter") {
        mattinsId = "the-day-draws-on-ferias";
        evensongId = "ye-choirs-of-new-jerusalem";
    } else if (hymnId === "ascension") {
        mattinsId = "eternal-monarch-king-most-high";
        evensongId = "eternal-monarch-king-most-high";
    } else if (hymnId === "whitsun") {
        mattinsId = "when-christ-our-lord-had-passed-once-more";
        evensongId = "rejoice-the-year-upon-its-way";
    } else if (hymnId === "advent") {
        mattinsId = "to-earth-descending-word-sublime";
        evensongId = "creator-of-the-stars-of-night";
    } else if (hymnId === "christmas") {
        mattinsId = "jesu-the-fathers-only-son";
        evensongId = "from-east-to-west-from-shore-to-shore";
    } else {
        mattinsId =
            day.dayOfTheWeek === "sun"
                ? "this-day-the-first-of-days-was-made"
                : day.dayOfTheWeek === "mon"
                ? "o-splendour-of-gods-glory-bright"
                : day.dayOfTheWeek === "tues"
                ? "the-wing-ed-herald-of-the-day"
                : day.dayOfTheWeek === "wed"
                ? "ye-clouds-and-darkness-hosts-of-night"
                : day.dayOfTheWeek === "thur"
                ? "lo-golden-light-rekindles-day"
                : day.dayOfTheWeek === "fri"
                ? "eternal-glory-of-the-sky"
                : day.dayOfTheWeek === "sat"
                ? "the-dawn-is-sprinkling-in-the-east"
                : "";
        evensongId =
            day.dayOfTheWeek === "sun"
                ? "o-blest-creator-of-the-light"
                : day.dayOfTheWeek === "mon"
                ? "o-boundless-wisdom-god-most-high"
                : day.dayOfTheWeek === "tues"
                ? "earths-mighty-maker-whose-command"
                : day.dayOfTheWeek === "wed"
                ? "most-holy-lord-and-god-of-heaven"
                : day.dayOfTheWeek === "thur"
                ? "almighty-god-who-from-the-flood"
                : day.dayOfTheWeek === "fri"
                ? "maker-of-man-who-from-thy-throne"
                : day.dayOfTheWeek === "sat"
                ? "creator-of-the-earth-and-sky"
                : "";
    }

    const id =
        hour === "mattins"
            ? mattinsId
            : hour === "evensong"
            ? evensongId
            : complineId;

    if (id === "") {
        return <Rubric content="(This Office Hymn is coming soon!)" />;
    }

    const hymn = HYMNS[id];

    let tuneLine = 0;
    const halves = hymn.text
        .slice(
            0,
            (verseLimit ?? hymn.text.length / hymn.tune.length) *
                hymn.tune.length
        )
        .map((line) => {
            let tuneIndex = 0;
            const half: PointedWord[] = [];

            line.forEach((word) => {
                half.push({
                    syllables: word.map((syllable) => {
                        const result = {
                            text: syllable,
                            notes: getNotesFromString(
                                hymn.tune[tuneLine][tuneIndex]
                            ),
                            long: tuneIndex === hymn.tune[tuneLine].length - 1,
                            dotBefore: false,
                        };
                        tuneIndex++;
                        return result;
                    }),
                });
            });

            tuneLine = (tuneLine + 1) % hymn.tune.length;

            return half;
        });

    const chants: PointedVerse[][] = [];
    for (let i = 0; i < halves.length; i += hymn.tune.length) {
        chants.push([]);
        for (let j = 0; j < hymn.tune.length; j += 2) {
            chants[chants.length - 1].push({
                firstHalf: halves[i + j],
                secondHalf: halves[i + j + 1],
            });
        }
    }

    chants[chants.length - 1].push({
        secondHalf: [
            {
                syllables: [
                    {
                        text: "A",
                        notes: getNotesFromString(hymn.amen[0]),
                        long: false,
                        dotBefore: false,
                    },
                    {
                        text: "men.",
                        notes: getNotesFromString(hymn.amen[1]),
                        long: true,
                        dotBefore: false,
                    },
                ],
            },
        ],
    });

    return (
        <StickyContainer id="hymn">
            <PlayableTitle
                id={hymn.title.replace(/ /g, "-")}
                title={hymn.title}
                player={<HymnPlayer hymn={hymn} />}
            />
            {chants.map((chant, chantIndex) => {
                return (
                    <Chant
                        pointedText={{ verses: chant, clef: hymn.clef }}
                        dropCap={chantIndex === 0}
                        boldMode="none"
                        yExtraOffset={
                            id === "sing-my-tongue-the-glorious-battle" ||
                            id === "thirty-years-among-us-dwelling" ||
                            id === "o-blest-creator-of-the-light"
                                ? 4
                                : 0
                        }
                        exportLines={exportLines}
                        tone={hymn.tone}
                    />
                );
            })}
        </StickyContainer>
    );
};
